import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Plus, ShieldCheck, ShieldOff, ShieldQuestion, Star, UserCog, Users as UsersIcon, Briefcase } from 'lucide-react';
import { AdminShell } from '@/components/layout/AdminShell';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api-client';
import { formatRelativeDate, getInitials, cn } from '@/lib/utils';
import { SPECIALTY_LABELS, SENIORITY_LABELS, type Specialty, type Seniority } from '@parecer/shared';

interface UserItem {
  userId: string;
  email: string;
  name: string;
  phone?: string;
  createdAt: string;
}

interface AdminItem extends UserItem {
  cognitoStatus?: string;
  enabled?: boolean;
}

interface LawyerCertification {
  id: string;
  title: string;
  issuer: string;
  issuedYear?: number;
  fileUrl?: string;
  verified?: boolean;
  verifiedAt?: string;
}

interface LawyerItem extends UserItem {
  oab: string;
  oabState: string;
  oabVerified: boolean;
  suspended?: boolean;
  bio?: string;
  specialties?: Specialty[];
  seniority?: Seniority;
  certifications?: LawyerCertification[];
  averageRating?: number;
  totalReviews?: number;
  totalCompletedTasks?: number;
}

type TabKey = 'admins' | 'lawyers' | 'users';

const TABS: { key: TabKey; label: string; icon: typeof UserCog }[] = [
  { key: 'admins', label: 'Admins', icon: UserCog },
  { key: 'lawyers', label: 'Advogados', icon: Briefcase },
  { key: 'users', label: 'Usuários', icon: UsersIcon },
];

export function AdminUsuarios() {
  const [tab, setTab] = useState<TabKey>('admins');
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <AdminShell>
      <div className="max-w-6xl">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-display-lg">Usuários</h1>
            <p className="text-muted-foreground mt-1">Admins, advogados e clientes da plataforma.</p>
          </div>
          {tab === 'admins' && (
            <Button variant="accent" onClick={() => setCreateOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Criar admin
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div role="tablist" aria-label="Filtrar por tipo" className="mt-6 border-b border-border/60 -mx-1 px-1 flex gap-1 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(key)}
                className={cn(
                  'inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
                  active
                    ? 'border-accent text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            );
          })}
        </div>

        <Input
          placeholder="Buscar por nome, email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="mt-6 max-w-md"
        />

        <div className="mt-6">
          {tab === 'admins' && <AdminsTab q={q} />}
          {tab === 'lawyers' && <LawyersTab q={q} />}
          {tab === 'users' && <UsersTab q={q} />}
        </div>
      </div>

      <CreateAdminDialog open={createOpen} onOpenChange={setCreateOpen} />
    </AdminShell>
  );
}

// ─── Admins tab ──────────────────────────────────────────────────────────────

function AdminsTab({ q }: { q: string }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'admins'],
    queryFn: () => api.get<{ items: AdminItem[] }>('/admin/admins'),
  });

  const resendMut = useMutation({
    mutationFn: (email: string) => api.post('/admin/users/resend-invite', { email }),
    onSuccess: (_d, email) =>
      toast({
        variant: 'success',
        title: 'Convite reenviado',
        description: `Novo email com senha temporária para ${email}.`,
      }),
    onError: (err) =>
      toast({
        variant: 'danger',
        title: 'Falha ao reenviar',
        description: err instanceof Error ? err.message : 'Erro inesperado',
      }),
  });

  const filtered = useMemo(() => {
    const arr = data?.items ?? [];
    if (!q) return arr;
    const n = q.toLowerCase();
    return arr.filter((a) => a.name?.toLowerCase().includes(n) || a.email?.toLowerCase().includes(n));
  }, [data, q]);

  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 text-left text-muted-foreground">
            <tr>
              <th className="p-4 font-medium">Pessoa</th>
              <th className="p-4 font-medium">Email</th>
              <th className="p-4 font-medium">Status</th>
              <th className="p-4 font-medium text-right">Criado em</th>
              <th className="p-4 font-medium text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {isLoading && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-muted-foreground">Carregando…</td>
              </tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-muted-foreground">
                  Nenhum admin encontrado.
                </td>
              </tr>
            )}
            {filtered.map((a) => {
              const pending = a.cognitoStatus === 'FORCE_CHANGE_PASSWORD';
              return (
                <tr key={a.userId} className="hover:bg-muted/30 transition-colors">
                  <td className="p-4 flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-accent/15 flex items-center justify-center text-xs">
                      {getInitials(a.name ?? a.email)}
                    </div>
                    <span className="font-medium">{a.name || '—'}</span>
                  </td>
                  <td className="p-4 text-muted-foreground">{a.email}</td>
                  <td className="p-4">
                    {pending ? (
                      <Badge variant="warning">Aguardando 1º login</Badge>
                    ) : a.cognitoStatus === 'CONFIRMED' ? (
                      <Badge variant="success">Ativo</Badge>
                    ) : a.enabled === false ? (
                      <Badge variant="danger">Desativado</Badge>
                    ) : (
                      <Badge variant="muted">{a.cognitoStatus ?? '—'}</Badge>
                    )}
                  </td>
                  <td className="p-4 text-right text-muted-foreground text-xs">
                    {a.createdAt ? formatRelativeDate(a.createdAt) : '—'}
                  </td>
                  <td className="p-4 text-right">
                    {pending && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        onClick={() => {
                          if (confirm(`Reenviar email com nova senha temporária para ${a.email}?`)) {
                            resendMut.mutate(a.email);
                          }
                        }}
                        loading={resendMut.isPending && resendMut.variables === a.email}
                      >
                        <Mail className="h-3.5 w-3.5" />
                        Reenviar convite
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
      {!isLoading && filtered.some((a) => a.cognitoStatus === 'FORCE_CHANGE_PASSWORD') && (
        <div className="border-t border-border/60 bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
          <strong className="text-foreground">Dica:</strong> contas "Aguardando 1º login" precisam fazer login
          com a senha do email (não usar "Esqueci a senha" — Cognito recusa reset antes do 1º login).
          A página de login pede uma nova senha automaticamente.
        </div>
      )}
    </Card>
  );
}

// ─── Lawyers tab ─────────────────────────────────────────────────────────────

function LawyersTab({ q }: { q: string }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [showOnlyUnverified, setShowOnlyUnverified] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'lawyers'],
    queryFn: () => api.get<{ items: LawyerItem[] }>('/admin/lawyers?limit=200'),
  });

  const patchMut = useMutation({
    mutationFn: (params: { lawyerId: string; body: Partial<{ oabVerified: boolean; suspended: boolean }> }) =>
      api.patch(`/admin/lawyers/${params.lawyerId}`, params.body),
    onSuccess: (_d, vars) => {
      toast({ variant: 'success', title: 'Advogado atualizado', description: vars.lawyerId.slice(0, 8) + '…' });
      qc.invalidateQueries({ queryKey: ['admin', 'lawyers'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
    onError: (err) =>
      toast({ variant: 'danger', title: 'Falha', description: err instanceof Error ? err.message : 'Erro' }),
  });

  const verifyCertMut = useMutation({
    mutationFn: (params: { lawyerId: string; certId: string; verified: boolean }) =>
      api.patch(`/admin/lawyers/${params.lawyerId}/certifications/${params.certId}`, {
        verified: params.verified,
      }),
    onSuccess: (_d, vars) => {
      toast({
        variant: 'success',
        title: vars.verified ? 'Certificação verificada' : 'Verificação removida',
        description: vars.certId,
      });
      qc.invalidateQueries({ queryKey: ['admin', 'lawyers'] });
    },
    onError: (err) =>
      toast({ variant: 'danger', title: 'Falha', description: err instanceof Error ? err.message : 'Erro' }),
  });

  const filtered = useMemo(() => {
    let arr = data?.items ?? [];
    if (showOnlyUnverified) arr = arr.filter((l) => !l.oabVerified);
    if (q) {
      const n = q.toLowerCase();
      arr = arr.filter(
        (l) =>
          l.name?.toLowerCase().includes(n) ||
          l.email?.toLowerCase().includes(n) ||
          l.oab?.includes(q),
      );
    }
    return arr;
  }, [data, q, showOnlyUnverified]);

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Button
          variant={showOnlyUnverified ? 'accent' : 'outline'}
          size="sm"
          onClick={() => setShowOnlyUnverified((v) => !v)}
        >
          <ShieldQuestion className="h-4 w-4 mr-2" />
          Só não-verificados
        </Button>
      </div>

      <div className="space-y-3">
        {isLoading && <p className="text-muted-foreground text-sm">Carregando…</p>}
        {!isLoading && filtered.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-muted-foreground text-sm">
              Nenhum advogado encontrado.
            </CardContent>
          </Card>
        )}
        {filtered.map((l) => (
          <Card key={l.userId}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center font-medium flex-none">
                  {getInitials(l.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-serif text-lg">{l.name}</h3>
                    {l.oabVerified ? (
                      <Badge variant="success" className="gap-1">
                        <ShieldCheck className="h-3 w-3" />OAB verificada
                      </Badge>
                    ) : (
                      <Badge variant="warning" className="gap-1">
                        <ShieldQuestion className="h-3 w-3" />OAB pendente
                      </Badge>
                    )}
                    {l.suspended && (
                      <Badge variant="danger" className="gap-1">
                        <ShieldOff className="h-3 w-3" />Suspenso
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    OAB/{l.oabState} {l.oab} · {l.email}
                    {l.seniority && (
                      <> · <span className="font-medium text-foreground">{SENIORITY_LABELS[l.seniority]}</span></>
                    )}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {l.specialties?.slice(0, 6).map((s) => (
                      <Badge key={s} variant="muted" className="text-[10px]">
                        {SPECIALTY_LABELS[s]}
                      </Badge>
                    ))}
                  </div>
                  {l.bio && <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{l.bio}</p>}
                  {l.certifications && l.certifications.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        Certificações ({l.certifications.length})
                      </p>
                      <ul className="space-y-1">
                        {l.certifications.map((c) => (
                          <li
                            key={c.id}
                            className="flex items-center justify-between gap-2 text-xs rounded border border-border/60 px-2.5 py-1.5 bg-muted/10"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">
                                {c.title}
                                <span className="text-muted-foreground font-normal">
                                  {' · '}{c.issuer}
                                  {c.issuedYear ? ` · ${c.issuedYear}` : ''}
                                </span>
                              </p>
                              {c.fileUrl && (
                                <a
                                  href={c.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[11px] text-accent hover:underline"
                                >
                                  Ver comprovante ↗
                                </a>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant={c.verified ? 'outline' : 'accent'}
                              className="h-7 px-2 text-[11px] gap-1 whitespace-nowrap"
                              onClick={() =>
                                verifyCertMut.mutate({
                                  lawyerId: l.userId,
                                  certId: c.id,
                                  verified: !c.verified,
                                })
                              }
                              loading={
                                verifyCertMut.isPending &&
                                verifyCertMut.variables?.lawyerId === l.userId &&
                                verifyCertMut.variables?.certId === c.id
                              }
                            >
                              {c.verified ? (
                                <>
                                  <ShieldCheck className="h-3 w-3" /> Verificada
                                </>
                              ) : (
                                <>
                                  <ShieldQuestion className="h-3 w-3" /> Verificar
                                </>
                              )}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                    {l.averageRating !== undefined && (
                      <span className="inline-flex items-center gap-1">
                        <Star className="h-3 w-3 fill-accent text-accent" />
                        {l.averageRating.toFixed(1)} ({l.totalReviews ?? 0})
                      </span>
                    )}
                    <span>{l.totalCompletedTasks ?? 0} entregas</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 flex-none">
                  <Button
                    size="sm"
                    variant={l.oabVerified ? 'outline' : 'accent'}
                    onClick={() => patchMut.mutate({ lawyerId: l.userId, body: { oabVerified: !l.oabVerified } })}
                    loading={patchMut.isPending && patchMut.variables?.lawyerId === l.userId}
                  >
                    {l.oabVerified ? 'Desverificar' : 'Verificar OAB'}
                  </Button>
                  <Button
                    size="sm"
                    variant={l.suspended ? 'outline' : 'danger'}
                    onClick={() => patchMut.mutate({ lawyerId: l.userId, body: { suspended: !l.suspended } })}
                  >
                    {l.suspended ? 'Reativar' : 'Suspender'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── Users tab (clientes) ────────────────────────────────────────────────────

function UsersTab({ q }: { q: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', { excludeRole: 'admins' }],
    queryFn: () => api.get<{ items: UserItem[] }>('/admin/users?limit=200&excludeRole=admins'),
  });

  const filtered = useMemo(() => {
    const arr = data?.items ?? [];
    if (!q) return arr;
    const n = q.toLowerCase();
    return arr.filter((u) => u.name?.toLowerCase().includes(n) || u.email?.toLowerCase().includes(n));
  }, [data, q]);

  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 text-left text-muted-foreground">
            <tr>
              <th className="p-4 font-medium">Pessoa</th>
              <th className="p-4 font-medium">Email</th>
              <th className="p-4 font-medium">Telefone</th>
              <th className="p-4 font-medium text-right">Cadastro</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {isLoading && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-muted-foreground">Carregando…</td>
              </tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-muted-foreground">
                  Nenhum cliente encontrado.
                </td>
              </tr>
            )}
            {filtered.map((u) => (
              <tr key={u.userId} className="hover:bg-muted/30 transition-colors">
                <td className="p-4 flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs">
                    {getInitials(u.name ?? u.email)}
                  </div>
                  <span className="font-medium">{u.name ?? '—'}</span>
                </td>
                <td className="p-4 text-muted-foreground">{u.email}</td>
                <td className="p-4 text-muted-foreground text-xs">{u.phone ?? '—'}</td>
                <td className="p-4 text-right text-muted-foreground text-xs">
                  {u.createdAt ? formatRelativeDate(u.createdAt) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

// ─── Create admin dialog ─────────────────────────────────────────────────────

const createAdminFormSchema = z.object({
  email: z.string().email('Email inválido'),
  name: z.string().min(2, 'Nome muito curto').max(120),
});
type CreateAdminForm = z.infer<typeof createAdminFormSchema>;

interface CreateAdminResponse {
  userId: string;
  email: string;
  name: string;
  role: string;
  message: string;
}

function CreateAdminDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const form = useForm<CreateAdminForm>({
    resolver: zodResolver(createAdminFormSchema),
    defaultValues: { email: '', name: '' },
  });

  const mut = useMutation({
    mutationFn: (body: CreateAdminForm) => api.post<CreateAdminResponse>('/admin/users', body),
    onSuccess: (data) => {
      toast({
        variant: 'success',
        title: 'Admin criado',
        description: data.message,
      });
      qc.invalidateQueries({ queryKey: ['admin', 'admins'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
      form.reset();
      onOpenChange(false);
    },
    onError: (err) =>
      toast({
        variant: 'danger',
        title: 'Falha ao criar admin',
        description: err instanceof Error ? err.message : 'Erro inesperado',
      }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Criar admin</DialogTitle>
          <DialogDescription>
            Cognito envia email com senha temporária. A pessoa troca a senha no primeiro login.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit((v) => mut.mutate(v))} className="space-y-4">
          <div>
            <Label htmlFor="admin-name">Nome completo</Label>
            <Input id="admin-name" autoComplete="name" className="mt-1.5" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-xs text-danger mt-1">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="admin-email">Email</Label>
            <Input id="admin-email" type="email" autoComplete="email" className="mt-1.5" {...form.register('email')} />
            {form.formState.errors.email && (
              <p className="text-xs text-danger mt-1">{form.formState.errors.email.message}</p>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="accent" loading={mut.isPending}>
              Criar e enviar convite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
